"use client";

import DiscCascadeCarousel, {
  type DiscCascadeItem,
} from "@/components/ui/disc-cascade-carousel";
import { useIsMobile } from "@/hooks/use-mobile";
import vinylRedBlack from "@/assets/vinyls/vinyl-red-black.webp";
import vinylPinkSplatter from "@/assets/vinyls/vinyl-pink-splatter.webp";
import vinylBlueSwirl from "@/assets/vinyls/vinyl-blue-swirl.webp";
import vinylAmerica from "@/assets/vinyls/vinyl-america.webp";
import vinylBrownSplatter from "@/assets/vinyls/vinyl-brown-splatter.webp";
import vinylGreen from "@/assets/vinyls/vinyl-green.webp";
import vinylSplatterWhite from "@/assets/vinyls/vinyl-splatter-white.webp";
import vinylOrangeBlue from "@/assets/vinyls/vinyl-orange-blue.webp";
import vinylOrange from "@/assets/vinyls/vinyl-orange.webp";
import vinylDarkRed from "@/assets/vinyls/vinyl-dark-red.webp";

// Prensagens coloridas reais enviadas pelo time — imagem pura, sem texto
// impresso por cima (label/fine vazios mantêm o disco só visual).
const records: DiscCascadeItem[] = [
  { src: vinylRedBlack, alt: "Vinil splatter vermelho e preto" },
  { src: vinylPinkSplatter, alt: "Vinil transparente com splatter rosa" },
  { src: vinylBlueSwirl, alt: "Vinil azul marmorizado" },
  { src: vinylAmerica, alt: "Vinil azul e vermelho" },
  { src: vinylBrownSplatter, alt: "Vinil amarelo com splatter marrom" },
  { src: vinylGreen, alt: "Vinil verde translúcido" },
  { src: vinylSplatterWhite, alt: "Vinil branco com splatter vermelho e verde" },
  { src: vinylOrangeBlue, alt: "Vinil laranja e azul" },
  { src: vinylOrange, alt: "Vinil laranja translúcido" },
  { src: vinylDarkRed, alt: "Vinil vermelho escuro marmorizado" },
].map((record) => ({ ...record, title: "", label: "", fine: "" }));

export function DiscCascadeShowcase() {
  const isMobile = useIsMobile();
  // No celular os discos são pequenos demais para uma seção de tela inteira:
  // a dobra encolhe e as capas crescem para preencher o quadro.
  const layout = isMobile
    ? {
        height: "64svh",
        stageTop: "50%",
        discSize: "clamp(170px, 62vw, 300px)",
        spacing: 0.88,
        rise: 0.18,
        fan: -6,
      }
    : {
        height: "86svh",
        stageTop: "48%",
        discSize: "clamp(180px, min(52vmin, 39vw), 440px)",
        spacing: 1.02,
        rise: 0.24,
        fan: -10,
      };

  return (
    // Curva de 50px nos dois cantos de cima e de baixo: o branco flutua sobre
    // o fundo escuro. O carrossel fica na mesma grade de conteúdo das outras
    // dobras (max-w-1120 + respiro lateral), então os discos não sangram para
    // fora do texto.
    <section
      aria-label="Discos da comunidade"
      className="landing-vinyl-fold relative z-10 isolate rounded-[50px] bg-[#1A1A1A] text-white"
      // clip-path em vez de overflow-hidden: um recorte arredondado sobre discos
      // em 3D faz o Chrome apagar e redesenhar os discos ao rolar (piscadas).
      style={{ clipPath: "inset(0 round 50px)" }}
    >

      {/* O carrossel ocupa a grade de conteúdo; os discos seguem sangrando para
          fora dela e só são cortados pela borda da dobra, nunca no meio. */}
      <div className="mx-auto max-w-[1120px] px-5 lg:px-10">
        <DiscCascadeCarousel
          items={records}
          height={layout.height}
          stageTop={layout.stageTop}
          discSize={layout.discSize}
          spacing={layout.spacing}
          rise={layout.rise}
          depth={0.42}
          yaw={22}
          fan={layout.fan}
          tilt={-6}
          roll={110}
          spin={24}
          sheen={0.6}
          bounce={0.22}
          duration={0.9}
          loop
          autoplay={3600}
          brand=""
          indexLabel=""
          details={false}
          reviews={false}
          controls
          hint="Arraste · ← →"
          frame={false}
          background="#1A1A1A"
          color="#ffffff"
          serif="var(--font-display)"
          sans="var(--font-sans)"
          display="var(--font-display)"
          defaultIndex={2}
          ariaLabel="Coleção de discos em vinil"
        />
      </div>
    </section>
  );
}
